import { useState, useCallback, useEffect, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../App';
import { useApi } from '../hooks/useApi';
import { api } from '../services/api';
import { syncPendingEntries, forceCreateEntry } from '@/services/syncService';
import { useNetwork } from '@/contexts/NetworkContext';
import {
  addPendingEntry,
  getPendingEntries,
  removePendingEntry,
  updatePendingEntry,
  setReferenceCache,
  getReferenceCache,
} from '@/services/offlineDb';
import type { PendingTimeEntry } from '@/services/offlineDb';
import { PageHeader } from '@/components/PageHeader';
import { EmptyState } from '@/components/EmptyState';
import { DataTable } from '@/components/DataTable';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Badge } from '@/components/ui/Badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { Skeleton } from '@/components/ui/Skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/Dialog';
import { Clock, Timer, Play, Square, Save, WifiOff, CalendarDays, Pencil, Trash2, FolderKanban, ChevronLeft, ChevronRight, Plus, CloudUpload } from 'lucide-react';
import type { Project, Task, TimeEntry } from '../types';

interface TaskOption { id: string; name: string; }

function statusVariant(status: string) {
  switch (status) { case 'APPROVED': return 'success' as const; case 'REJECTED': return 'destructive' as const; case 'SUBMITTED': return 'warning' as const; default: return 'secondary' as const; }
}

function elapsedSince(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function parseDurationToMinutes(input: string): number {
  if (input.includes(':')) {
    const [h, m] = input.split(':').map(Number);
    return (h || 0) * 60 + (m || 0);
  }
  const decimal = parseFloat(input);
  if (isNaN(decimal)) return 0;
  return Math.round(decimal * 60);
}

export default function TimeEntriesPage() {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext)!;
  const isAdmin = user?.globalRole === 'ADMIN';
  const { isOnline } = useNetwork();
  const { data: entries, loading: eLoading, error: eError, refetch: refetchEntries } = useApi<TimeEntry[]>(() => api('/time-entries'), []);
  const { data: projects, loading: pLoading } = useApi<Project[]>(() => api('/projects'), []);

  const [projectId, setProjectId] = useState('');
  const [taskId, setTaskId] = useState('');
  const [tasks, setTasks] = useState<TaskOption[]>([]);
  const [duration, setDuration] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [note, setNote] = useState('');
  const [running, setRunning] = useState(false);
  const [timerDisplay, setTimerDisplay] = useState('00:00:00');
  const [startedAt, setStartedAt] = useState('');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState('');
  const [pendingEntries, setPendingEntries] = useState<PendingTimeEntry[]>([]);
  const [cachedProjects, setCachedProjects] = useState<Project[]>([]);

  const [editOpen, setEditOpen] = useState(false);
  const [editEntry, setEditEntry] = useState<TimeEntry | null>(null);
  const [editDuration, setEditDuration] = useState('');
  const [editDate, setEditDate] = useState('');
  const [editNote, setEditNote] = useState('');
  const [editFormError, setEditFormError] = useState('');

  const [activeTab, setActiveTab] = useState('log');
  const [weekOffset, setWeekOffset] = useState(0);

  const allProjects = projects && projects.length > 0 ? projects : cachedProjects;
  const refetchRef = useRef(refetchEntries);
  refetchRef.current = refetchEntries;

  function weekDates(offset: number): Date[] {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1) - offset * 7;
    const monday = new Date(today);
    monday.setDate(diff);
    monday.setHours(0, 0, 0, 0);
    const days: Date[] = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      d.setHours(0, 0, 0, 0);
      days.push(d);
    }
    return days;
  }

  const handleAddFromWeekView = (dayDate: string) => {
    setDate(dayDate);
    setActiveTab('log');
  };

  // Load pending entries from IndexedDB on mount
  const loadPending = useCallback(async () => {
    const p = await getPendingEntries();
    setPendingEntries(p);
  }, []);

  useEffect(() => {
    loadPending();
  }, [loadPending]);

  // Listen for sync completion to refetch server entries and update pending list
  useEffect(() => {
    const onSync = () => {
      refetchRef.current();
      loadPending();
    };
    window.addEventListener('jani-sync-complete', onSync);
    return () => window.removeEventListener('jani-sync-complete', onSync);
  }, [loadPending]);

  // Cache projects when loaded online
  useEffect(() => {
    if (projects && projects.length > 0) {
      setReferenceCache('projects', projects).catch(() => {});
    }
  }, [projects]);

  // Load cached projects when offline and none loaded
  useEffect(() => {
    if (!isOnline && (projects === null || projects.length === 0) && !pLoading) {
      getReferenceCache('projects').then((cached) => {
        if (cached) setCachedProjects(cached as Project[]);
      }).catch(() => {});
    }
  }, [isOnline, projects, pLoading]);

  useEffect(() => {
    const saved = localStorage.getItem('jani_timer');
    if (saved) {
      try {
        const { projectId: pid, taskId: tid, startedAt: s } = JSON.parse(saved);
        setProjectId(pid); loadTasks(pid).then(() => setTaskId(tid)); setRunning(true); setStartedAt(s);
      } catch { localStorage.removeItem('jani_timer'); }
    }
  }, []);

  useEffect(() => {
    if (!running || !startedAt) return;
    const id = setInterval(() => setTimerDisplay(elapsedSince(startedAt)), 1000);
    return () => clearInterval(id);
  }, [running, startedAt]);

  const loadTasks = useCallback(async (pid: string) => {
    if (!pid) { setTasks([]); return; }
    try {
      if (isOnline) {
        const data = await api<Task[]>(`/projects/${pid}/tasks`);
        setTasks(data || []);
        setReferenceCache(`tasks:${pid}`, data || []).catch(() => {});
      } else {
        const cached = await getReferenceCache(`tasks:${pid}`) as TaskOption[];
        setTasks(cached || []);
      }
    } catch { setTasks([]); }
  }, [isOnline]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault(); setFormError(''); setBusy(true);
    try {
      if (!isOnline) {
        const localId = `local_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
        const newEntry: PendingTimeEntry = {
          localId,
          userId: user!.id,
          projectId,
          taskId,
          date,
          duration,
          note,
          status: 'PENDING',
          createdAt: Date.now(),
        };
        await addPendingEntry(newEntry);
        setPendingEntries((prev) => [...prev, newEntry]);
        setDuration(''); setNote('');
      } else {
        await api('/time-entries', { method: 'POST', body: JSON.stringify({ projectId, taskId, duration, date, note }) });
        setDuration(''); setNote('');
        refetchEntries();
      }
    } catch (err: any) { setFormError(err.message); } finally { setBusy(false); }
  };

  const handleStartTimer = async () => {
    if (!projectId || !taskId) return;
    try {
      await api('/time-entries/timer/start', { method: 'POST', body: JSON.stringify({ projectId, taskId, note }) });
      const now = new Date().toISOString(); setRunning(true); setStartedAt(now);
      localStorage.setItem('jani_timer', JSON.stringify({ projectId, taskId, note, startedAt: now }));
    } catch (err: any) { setFormError(err.message); }
  };

  const handleStopTimer = async () => {
    try {
      await api('/time-entries/timer/stop', { method: 'POST', body: '{}' });
      setRunning(false); setTimerDisplay('00:00:00'); localStorage.removeItem('jani_timer'); refetchEntries();
    } catch (err: any) { setFormError(err.message); }
  };

  const handleOpenEdit = (entry: TimeEntry) => {
    setEditEntry(entry);
    setEditDuration((entry.minutes / 60).toFixed(2));
    setEditDate(entry.date.split('T')[0]);
    setEditNote(entry.note || '');
    setEditFormError('');
    setEditOpen(true);
  };

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editEntry) return;
    setBusy(true); setEditFormError('');
    try {
      await api(`/time-entries/${editEntry.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ duration: editDuration, date: editDate, note: editNote }),
      });
      setEditOpen(false);
      setEditEntry(null);
      refetchEntries();
    } catch (err: any) {
      setEditFormError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (entryId: string) => {
    if (entryId.startsWith('local_')) {
      const localId = entryId.replace('local_', '');
      try {
        await removePendingEntry(localId);
        setPendingEntries((prev) => prev.filter((p) => p.localId !== localId));
      } catch (err: any) {
        setFormError(err.message || 'Delete failed');
      }
      return;
    }
    try {
      await api(`/time-entries/${entryId}`, { method: 'DELETE' });
      refetchEntries();
    } catch (err: any) {
      setFormError(err.message || 'Delete failed');
    }
  };

  const handleForceCreate = async (entry: PendingTimeEntry) => {
    setBusy(true);
    try {
      const success = await forceCreateEntry(entry);
      if (success) {
        setPendingEntries((prev) => prev.filter((p) => p.localId !== entry.localId));
        refetchEntries();
      } else {
        const updated = await getPendingEntries();
        setPendingEntries(updated);
        setFormError('Failed to force-create entry. It is now marked as FAILED.');
      }
    } catch (err: any) {
      setFormError(err.message || 'Force create failed');
    } finally {
      setBusy(false);
    }
  };

  const handleRetryEntry = async (localId: string) => {
    try {
      await updatePendingEntry(localId, { status: 'PENDING', error: undefined });
      await syncPendingEntries();
      const updated = await getPendingEntries();
      setPendingEntries(updated);
      refetchEntries();
    } catch (err: any) {
      setFormError(err.message || 'Retry failed');
    }
  };

  function pendingToDisplay(pending: PendingTimeEntry): TimeEntry {
    const project = allProjects.find((p) => p.id === pending.projectId);
    const task = project?.tasks?.find((t) => t.id === pending.taskId);
    return {
      id: `local_${pending.localId}`,
      userId: pending.userId,
      projectId: pending.projectId,
      taskId: pending.taskId,
      date: pending.date,
      minutes: parseDurationToMinutes(pending.duration),
      note: pending.note,
      billable: true,
      source: 'OFFLINE',
      status: 'PENDING',
      createdAt: new Date(pending.createdAt).toISOString(),
      project,
      task,
    };
  }

  const displayEntries = [...(entries || []), ...pendingEntries.map(pendingToDisplay)];

  const columns = [
    { key: 'date', header: t('date'), render: (e: TimeEntry) => new Date(e.date).toLocaleDateString() },
    { key: 'project', header: t('project'), render: (e: TimeEntry) => e.project?.name },
    { key: 'task', header: t('task'), render: (e: TimeEntry) => e.task?.name },
    { key: 'duration', header: t('duration'), render: (e: TimeEntry) => <span className="font-mono font-medium text-[var(--color-primary)]">{(e.minutes / 60).toFixed(2)}h</span> },
    { key: 'note', header: t('note'), render: (e: TimeEntry) => <span className="text-[var(--color-muted-foreground)]">{e.note}</span> },
    { key: 'status', header: t('status'), render: (e: TimeEntry) => <Badge variant={statusVariant(e.status)}>{e.status}</Badge> },
    { key: 'actions', header: '', className: 'w-20', render: (e: TimeEntry) => {
      if (e.id.startsWith('local_')) {
        return (
          <div className="flex items-center gap-1">
            <span className="text-xs text-amber-500 font-medium">Offline</span>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-[var(--color-destructive)] hover:text-destructive" onClick={() => handleDelete(e.id)} aria-label="Delete"><Trash2 className="h-4 w-4" /></Button>
          </div>
        );
      }
      if (!isOnline) return null;
      return (e.status === 'DRAFT' || isAdmin) && (e.userId === user?.id || isAdmin) ? (
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleOpenEdit(e)} aria-label="Edit"><Pencil className="h-4 w-4" /></Button>
          <Button data-testid="delete-entry-button" variant="ghost" size="icon" className="h-8 w-8 text-[var(--color-destructive)] hover:text-destructive" onClick={() => handleDelete(e.id)} aria-label="Delete"><Trash2 className="h-4 w-4" /></Button>
        </div>
      ) : null;
    }},
  ];

  const mobileRender = (e: TimeEntry) => {
    const isLocal = e.id.startsWith('local_');
    const allowEdit = !isLocal && isOnline && (e.status === 'DRAFT' || isAdmin) && (e.userId === user?.id || isAdmin);
    return (
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[var(--color-primary)]/10 text-[var(--color-primary)]"><Clock className="h-4 w-4" /></div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium">{e.project?.name}</div>
            <div className="flex items-center gap-1">
              <Badge variant={statusVariant(e.status)}>{e.status}</Badge>
              {isLocal && (
                <span className="text-xs text-amber-500 font-medium">Offline</span>
              )}
              {allowEdit && (
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleOpenEdit(e)} aria-label="Edit"><Pencil className="h-3.5 w-3.5" /></Button>
              )}
            </div>
          </div>
          <div className="text-xs text-[var(--color-muted-foreground)]">{e.task?.name} · {new Date(e.date).toLocaleDateString()}</div>
          <div className="mt-1 flex items-center gap-2 text-sm">
            <span className="font-mono font-medium text-[var(--color-primary)]">{(e.minutes / 60).toFixed(2)}h</span>
            {e.note && <span className="text-xs text-[var(--color-muted-foreground)] truncate">{e.note}</span>}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="animate-in space-y-6">
      {!isOnline && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-900" data-testid="offline-banner">
          <WifiOff className="h-4 w-4" /> You are offline. Only time tracking is available.
        </div>
      )}
      <PageHeader title={t('timeTracking') || 'Time Entries'} />

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="log"><Save className="mr-2 h-4 w-4" /> Log Entry</TabsTrigger>
          <TabsTrigger value="timer"><Timer className="mr-2 h-4 w-4" /> Timer</TabsTrigger>
          <TabsTrigger value="week"><CalendarDays className="mr-2 h-4 w-4" /> Week View</TabsTrigger>
        </TabsList>
        <TabsContent value="log" className="mt-4">
          <Card>
            <CardContent className="pt-6">
              <form onSubmit={handleCreate} className="space-y-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[var(--color-muted-foreground)]"><FolderKanban className="h-3.5 w-3.5" /> Project</Label>
                    <Select value={projectId} onValueChange={v => { setProjectId(v); loadTasks(v); }}>
                      <SelectTrigger><SelectValue placeholder={pLoading ? t('loading') : 'Project'} /></SelectTrigger>
                      <SelectContent>{(allProjects || []).map(p => <SelectItem key={p.id} value={p.id}>{p.name} {allProjects === cachedProjects && !projects ? '(cached)' : ''}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-[var(--color-muted-foreground)]">Task</Label>
                    <Select value={taskId} onValueChange={setTaskId} disabled={!projectId}>
                      <SelectTrigger><SelectValue placeholder="Task" /></SelectTrigger>
                      <SelectContent>{tasks.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[var(--color-muted-foreground)]"><CalendarDays className="h-3.5 w-3.5" /> Date</Label>
                    <Input type="date" value={date} onChange={e => setDate(e.target.value)} required />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[var(--color-muted-foreground)]"><Clock className="h-3.5 w-3.5" /> Duration</Label>
                    <Input data-testid="duration-input" type="text" value={duration} onChange={e => setDuration(e.target.value)} placeholder="e.g. 2.5 or 2:30" required />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[var(--color-muted-foreground)]"><Pencil className="h-3.5 w-3.5" /> Note</Label>
                    <Input data-testid="note-input" type="text" value={note} onChange={e => setNote(e.target.value)} placeholder="Optional" />
                  </div>
                </div>
                {formError && <Badge variant="destructive">{formError}</Badge>}
                <Button data-testid="save-entry-button" type="submit" disabled={busy || running} loading={busy} className="gap-2"><Save className="h-4 w-4" /> {t('save')}</Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="timer" className="mt-4">
          <Card className="text-center">
            <CardContent className="py-12">
              <div className="mb-6 inline-flex h-20 w-20 items-center justify-center rounded-full bg-[var(--color-primary)]/10 text-[var(--color-primary)]">
                <Timer className="h-10 w-10" />
              </div>
              <div className="mb-8 text-5xl font-bold text-[var(--color-primary)] font-mono tracking-tight">{timerDisplay}</div>
              <div className="flex flex-wrap items-center justify-center gap-3">
                {!running ? (
                  <Button data-testid="start-timer-button" size="lg" className="gap-2" onClick={handleStartTimer} disabled={!projectId || !taskId || !isOnline}><Play className="h-5 w-5" /> Start Timer</Button>
                ) : (
                  <Button data-testid="stop-timer-button" size="lg" variant="destructive" className="gap-2" onClick={handleStopTimer}><Square className="h-5 w-5" /> Stop Timer</Button>
                )}
              </div>
              <div className="mt-4 text-sm text-[var(--color-muted-foreground)]">
                {projectId ? `Project: ${allProjects?.find(p => p.id === projectId)?.name || '-'}` : 'Select a project and task before starting the timer.'}
                {!isOnline && <div className="mt-1 text-amber-500 text-xs">Timer requires an internet connection.</div>}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="week" className="mt-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-2">
                  <Button data-testid="prev-week-button" variant="outline" size="sm" onClick={() => setWeekOffset(w => w - 1)} aria-label="Previous week">
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button data-testid="today-week-button" variant="outline" size="sm" onClick={() => setWeekOffset(0)}>Today</Button>
                  <Button data-testid="next-week-button" variant="outline" size="sm" onClick={() => setWeekOffset(w => w + 1)} aria-label="Next week">
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
                <span className="text-sm font-semibold">
                  {weekDates(weekOffset)[0].toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })} – {weekDates(weekOffset)[6].toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-7 gap-3">
                {(weekDates(weekOffset)).map((d) => {
                  const dateStr = d.toISOString().split('T')[0];
                  const dayEntries = (entries || []).filter((e) => e.date && e.date.startsWith(dateStr));
                  const totalHours = dayEntries.reduce((s, e) => s + (e.minutes / 60), 0);
                  return (
                    <div key={dateStr} className="flex flex-col rounded-lg border border-[var(--color-border)] p-3 gap-2 min-h-[160px]">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold">{d.toLocaleDateString(undefined, { weekday: 'short' })}</span>
                        <span className="text-xs text-[var(--color-muted-foreground)]">{d.getDate()}</span>
                      </div>
                      <div className="text-lg font-bold font-mono text-[var(--color-primary)]">{totalHours.toFixed(1)}h</div>
                      <div className="flex-1 space-y-1">
                        {dayEntries.map((e) => (
                          <button key={e.id} onClick={() => handleOpenEdit(e)} className="w-full text-left rounded-md bg-slate-50 p-1.5 hover:bg-slate-100 transition-colors">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-medium truncate">{e.project?.name}</span>
                              <span className="text-xs font-mono text-[var(--color-primary)]">{(e.minutes / 60).toFixed(1)}h</span>
                            </div>
                          </button>
                        ))}
                      </div>
                      <Button data-testid={`add-day-${dateStr}`} variant="ghost" size="sm" className="mt-auto gap-1 text-[var(--color-primary)]" onClick={() => handleAddFromWeekView(dateStr)}>
                        <Plus className="h-3.5 w-3.5" /> Add
                      </Button>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {eError && <Badge variant="destructive">{eError}</Badge>}
      {eLoading ? <div className="space-y-2"><Skeleton className="h-10 w-full" /><Skeleton className="h-12 w-full" /></div> : (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-base"><Clock className="h-4 w-4 text-[var(--color-primary)]" /> Entries</CardTitle></CardHeader>
          <CardContent>
            <DataTable
              columns={columns}
              data={displayEntries}
              keyExtractor={(e) => e.id}
              mobileRender={mobileRender}
              emptyState={<EmptyState icon={Clock} title={t('noData') || 'No entries'} description="Log your first time entry to get started." />}
            />
          </CardContent>
        </Card>
      )}

      {pendingEntries.length > 0 && (
        <Card data-testid="pending-entries-section">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <CloudUpload className="h-4 w-4 text-[var(--color-primary)]" /> Pending Entries
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {pendingEntries.map((p) => (
                <div key={p.localId} className="flex flex-col gap-2 rounded-md border border-[var(--color-border)] p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Badge variant={p.status === 'FAILED' || p.status === 'CONFLICT' ? 'destructive' : 'warning'}>{p.status}</Badge>
                      <span className="text-sm">{p.date} — {p.duration}h</span>
                      <span className="text-xs text-[var(--color-muted-foreground)]">{allProjects?.find(pr => pr.id === p.projectId)?.name || p.projectId}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {p.status === 'CONFLICT' && (
                        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => handleForceCreate(p)}>Keep both</Button>
                      )}
                      {p.status === 'FAILED' && (
                        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => handleRetryEntry(p.localId)}>Retry</Button>
                      )}
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-[var(--color-destructive)]" onClick={() => handleDelete(`local_${p.localId}`)} aria-label="Delete"><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </div>
                  {p.error && (
                    <div className="text-xs text-[var(--color-destructive)]">{p.error}</div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Time Entry</DialogTitle></DialogHeader>
          <form onSubmit={handleSaveEdit} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[var(--color-muted-foreground)]"><CalendarDays className="h-3.5 w-3.5" /> Date</Label>
                <Input type="date" value={editDate} onChange={e => setEditDate(e.target.value)} required />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[var(--color-muted-foreground)]"><Clock className="h-3.5 w-3.5" /> Duration</Label>
                <Input type="text" value={editDuration} onChange={e => setEditDuration(e.target.value)} placeholder="e.g. 2.5 or 2:30" required />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[var(--color-muted-foreground)]"><Pencil className="h-3.5 w-3.5" /> Note</Label>
                <Input type="text" value={editNote} onChange={e => setEditNote(e.target.value)} />
              </div>
            </div>
            {editFormError && <Badge variant="destructive">{editFormError}</Badge>}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
              <Button type="submit" loading={busy} disabled={busy}>Save</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

    </div>
  );
}
